import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { PersonDuplicateCheckRequest, PersonOrg } from '@ndvr/customer-api';

import { CustomerApi } from 'API';
import { Toast } from 'widgets';

import { PersonFormValues } from './person-form-model';

export const findPersonDuplicates = async (tenantGUID: string, request: PersonDuplicateCheckRequest) => {
    const [result] = await CustomerApi({ hideError: true }).findPotentialPersonDuplicates(tenantGUID, request);
    if (!result) throw new Error('Duplicate check returned no result');
    return result;
};

export const checkPersonDuplicates = (tenantGUID: string, person: PersonFormValues) =>
    findPersonDuplicates(tenantGUID, {
        firstName: person.firstName,
        lastName: person.lastName,
        emails: [person.primaryEmail, person.otherEmail].filter((value): value is string => !!value),
        phones: [person.personalPhone, person.businessPhone].filter((value): value is string => !!value),
    });

export const usePotentialPersonDuplicates = (scopeKey: string) => {
    const request = useRef<object>();
    const confirmedCreate = useRef<() => void>();
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState<PersonOrg[]>();

    const cancel = useCallback(() => {
        request.current = undefined;
        confirmedCreate.current = undefined;
        setResult(undefined);
        setBusy(false);
    }, []);

    useLayoutEffect(() => {
        cancel();
        return () => {
            request.current = undefined;
            confirmedCreate.current = undefined;
        };
    }, [scopeKey, cancel]);

    const check = useCallback(
        async (load: () => Promise<PersonOrg[]>, create: () => void) => {
            if (request.current) return;
            const current = {};
            request.current = current;
            setBusy(true);
            let response: PersonOrg[];
            try {
                response = await load();
            } catch (error) {
                if (request.current === current) {
                    cancel();
                    Toast.forError(error, "Couldn't check for duplicates. Please try again.");
                }
                return;
            }
            if (request.current !== current) return;
            setBusy(false);
            if (response.length) {
                // Keep the submitted draft, rather than reading the form again on confirmation.
                confirmedCreate.current = create;
                setResult(response);
            } else {
                cancel();
                create();
            }
        },
        [cancel],
    );

    const proceed = () => {
        const create = confirmedCreate.current;
        if (!create) return;
        cancel();
        create();
    };

    return { check, result, busy, cancel, proceed };
};
